/**
 * Minmaxify Validation Separation Fix
 * 
 * This script separates validation for minimum product quantities (during add-to-cart)
 * from minimum order total validation (during checkout).
 */

(function() {
  // Configuration
  const DEBUG = true; // Enable logging for troubleshooting
  
  // Debug logging function
  function log(message, data) {
    if (DEBUG) {
      console.log(`[ValidationFix] ${message}`, data || '');
    }
  }
  
  // Wait for DOM to be fully loaded
  document.addEventListener('DOMContentLoaded', function() {
    log('Initializing validation separation');
    
    // Set up a mutation observer to catch error messages
    setupErrorMessageObserver();
    
    // Override checkout button behavior
    setupCheckoutButtonOverride();
  });
  
  // Set up mutation observer to catch error messages
  function setupErrorMessageObserver() {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // Check if this is an error dialog
              if (isErrorDialog(node)) {
                handleErrorDialog(node);
              }
              
              // Also check for error dialogs inside this node
              const errorDialogs = node.querySelectorAll('.modal, .dialog, [role="dialog"]');
              errorDialogs.forEach(dialog => {
                if (isErrorDialog(dialog)) {
                  handleErrorDialog(dialog);
                }
              });
            }
          });
        }
      });
    });
    
    // Start observing the body
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    log('Error dialog observer started');
    
    // Also check periodically for error dialogs
    setInterval(checkForErrorDialogs, 500);
  }
  
  // Check if an element is an error dialog
  function isErrorDialog(element) {
    // Check if it's a dialog/modal
    const isDialog = element.classList && 
                     (element.classList.contains('modal') || 
                      element.classList.contains('dialog') ||
                      element.hasAttribute('role') && element.getAttribute('role') === 'dialog');
    
    if (!isDialog) return false;
    
    // Check if it contains minimum order quantity text
    const content = element.textContent || '';
    return content.includes('minimum order quantity') || 
           content.includes('must have at least') ||
           content.includes('Must have at least $100 in total');
  }
  
  // Regular check for error dialogs
  function checkForErrorDialogs() {
    const dialogs = document.querySelectorAll('.modal, .dialog, [role="dialog"]');
    
    dialogs.forEach(dialog => {
      if (isErrorDialog(dialog) && isVisible(dialog)) {
        handleErrorDialog(dialog);
      }
    });
  }
  
  // Check if an element is visible
  function isVisible(element) {
    return element.style.display !== 'none' && 
           element.style.visibility !== 'hidden' &&
           element.offsetParent !== null;
  }
  
  // Handle error dialog content
  function handleErrorDialog(dialog) {
    const content = dialog.textContent || '';
    
    // Check if this is a minimum order total error
    if (content.includes('Must have at least $100 in total')) {
      // Check if we're in the checkout process
      const isCheckoutAttempt = wasCheckoutButtonClicked();
      
      // If this is not a checkout attempt, suppress the message
      if (!isCheckoutAttempt) {
        log('Suppressing minimum order total message during add-to-cart');
        
        // Click the OK button or hide the dialog
        const okButton = dialog.querySelector('button.ok, button.OK, button.close, button.dismiss');
        if (okButton) {
          okButton.click();
        } else {
          dialog.style.display = 'none';
          
          // Also hide backdrop if present
          const backdrop = document.querySelector('.modal-backdrop, .overlay, .dialog-backdrop');
          if (backdrop) {
            backdrop.style.display = 'none';
          }
        }
      }
    }
    // Handle item quantity errors - keep these during add-to-cart but check if already fixed
    else if (content.includes('must have at least') || content.includes('minimum order quantity')) {
      // Extract product-specific errors
      const productErrorItems = extractProductErrors(dialog);
      
      // Check if all products already have correct quantities
      checkProductQuantities(productErrorItems)
        .then(allQuantitiesCorrect => {
          if (allQuantitiesCorrect) {
            log('All products already have correct quantities, suppressing error');
            
            // Click the OK button or hide the dialog
            const okButton = dialog.querySelector('button.ok, button.OK, button.close, button.dismiss');
            if (okButton) {
              okButton.click();
            } else {
              dialog.style.display = 'none';
              
              // Also hide backdrop if present
              const backdrop = document.querySelector('.modal-backdrop, .overlay, .dialog-backdrop');
              if (backdrop) {
                backdrop.style.display = 'none';
              }
            }
          }
        });
    }
  }
  
  // Extract product-specific errors from dialog
  function extractProductErrors(dialog) {
    const errors = [];
    
    // Check for list items
    const listItems = dialog.querySelectorAll('li');
    if (listItems.length > 0) {
      listItems.forEach(item => {
        const text = item.textContent || '';
        const match = text.match(/([^:]+):\s*Must have at least (\d+) of this item/i);
        
        if (match && match[1] && match[2]) {
          errors.push({
            product: match[1].trim(),
            minQuantity: parseInt(match[2], 10)
          });
        }
      });
    } 
    // Check for direct text if no list items
    else {
      const content = dialog.textContent || '';
      const match = content.match(/([^:]+):\s*Must have at least (\d+) of this item/i);
      
      if (match && match[1] && match[2]) {
        errors.push({
          product: match[1].trim(),
          minQuantity: parseInt(match[2], 10)
        });
      }
    }
    
    return errors;
  }
  
  // Check if products already have correct quantities in cart
  function checkProductQuantities(productErrors) {
    if (productErrors.length === 0) {
      return Promise.resolve(false);
    }
    
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const items = cart.items || [];
        
        // Check each product error
        const results = productErrors.map(error => {
          // Find matching item in cart
          const cartItem = items.find(item => {
            return item.title.includes(error.product) || error.product.includes(item.title);
          });
          
          // Check if quantity is correct
          return cartItem && cartItem.quantity >= error.minQuantity;
        });
        
        // All quantities must be correct
        return results.every(result => result === true);
      })
      .catch(error => {
        log('Error checking cart:', error);
        return false;
      });
  }
  
  // Track checkout button clicks
  let checkoutClicked = false;
  
  function wasCheckoutButtonClicked() {
    return checkoutClicked;
  }
  
  // Reset checkout clicked state
  function resetCheckoutClicked() {
    setTimeout(() => {
      checkoutClicked = false;
    }, 5000); // Reset after 5 seconds
  }
  
  // Override checkout button behavior
  function setupCheckoutButtonOverride() {
    // Track checkout button clicks
    document.addEventListener('click', function(event) {
      const target = event.target;
      
      // Check if this is a checkout button
      const isCheckoutButton = 
        target.classList && target.classList.contains('cart__checkout') ||
        target.getAttribute('name') === 'checkout' ||
        target.textContent.includes('Checkout') ||
        target.closest('button[name="checkout"]') !== null;
      
      if (isCheckoutButton) {
        log('Checkout button clicked');
        checkoutClicked = true;
        resetCheckoutClicked();
      }
    }, true); // Use capturing phase to catch events early
  }
})();